import { useState } from 'react';
import { useAccount, useHeartbeatRun as useRunQuery, useHeartbeatRuns, useHeartbeatTimeline, useHeartbeatGrid, useTakePulse, useRetryLabeling } from '../../../services/queries';
import { derivePageState, ACTIVE } from './pageState';
import { useHeartbeatRun } from './useHeartbeatRun';
import { useVoices, REPLY_FILTERS } from './useVoices';
import { reviewIsActive } from './useHeartbeatContext';

export { REPLY_FILTERS };

export function useHeartbeatPage(accountId) {
	const [range] = useState(() => ({ bucket: 'day' }));
	// The account itself, so the page can say whose pulse this is and the
	// switcher can resolve the client from a deep link.
	const accountQ = useAccount(accountId);

	// The runs list feeds RunHistory, and its row for the run in flight reports
	// "0 replies · 0 posts" until something refetches it. Nothing did: the list
	// has a one-minute staleTime and was only invalidated when a run reached a
	// terminal status. So the history row stayed at zero for a run's whole
	// duration even when the live panel above it was working. Polled slower than
	// the run detail (10s vs 5s) because it is a summary row, not the live panel.
	const runsQ = useHeartbeatRuns(accountId, {
		refetchInterval: (q) => {
			const newest = (q.state.data || [])[0];
			return (newest && ACTIVE.includes(newest.status)) || (q.state.data || []).some((run) => reviewIsActive(run.contextReview)) ? 10000 : false;
		},
		refetchIntervalInBackground: false,
	});
	const runs = runsQ.data || [];
	const latestRun = runs[0] || null;
	const liveRunId = latestRun && ACTIVE.includes(latestRun.status) ? latestRun._id : runs.find((run) => reviewIsActive(run.contextReview))?._id || null;
	const live = useHeartbeatRun(liveRunId, accountId);

	const state = derivePageState({ accountId, runs });
	const lastTerminalRow = runs.find((r) => !ACTIVE.includes(r.status)) || null;
	const terminalQ = useRunQuery(lastTerminalRow?._id, { refetchInterval: (q) => reviewIsActive(q.state.data?.contextReview) ? 5000 : false });
	const lastTerminal = terminalQ.data?._id === lastTerminalRow?._id ? terminalQ.data : lastTerminalRow;
	const previousTerminal = runs.filter((r) => !ACTIVE.includes(r.status))[1] || null;

	const timelineQ = useHeartbeatTimeline(accountId, range, { enabled: !!accountId && state !== 'empty' });
	const gridQ = useHeartbeatGrid(accountId, lastTerminal ? { runId: lastTerminal._id, ...(lastTerminal.readingRevision ? { revision: lastTerminal.readingRevision } : {}) } : undefined, { enabled: !!lastTerminal });
	const prevGridQ = useHeartbeatGrid(accountId, previousTerminal ? { runId: previousTerminal._id } : undefined, { enabled: !!previousTerminal });

	const voicesState = useVoices(accountId, lastTerminal?._id, lastTerminal?.readingRevision);

	const takePulse = useTakePulse(accountId);
	const retry = useRetryLabeling(accountId);

	return {
		state, runs, latestRun, lastTerminal, live,
		account: accountQ.data || null, accountLoading: accountQ.isLoading,
		timeline: timelineQ.data?.points || [], timelineLoading: timelineQ.isLoading,
		grid: gridQ.data?.rows || [], tiles: gridQ.data?.tiles || {}, previousTiles: prevGridQ.data?.tiles || null, gridLoading: gridQ.isLoading,
		...voicesState,
		takePulse: (requestedPosts) => takePulse.mutateAsync({ requestedPosts }), taking: takePulse.isPending, takeError: takePulse.error,
		retryLabeling: (runId) => retry.mutateAsync(runId), retrying: retry.isPending,
	};
}
